import { Link, useNavigate } from 'react-router-dom';
import GiftsShopModal from '../../components/gifts/GiftsShopModal';
import React, { useState, useEffect } from 'react';
import Policy from '../../components/shared/Policy';
import config from '../../config';
import { useDispatch, useSelector } from 'react-redux';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { useSubscription } from '../../hooks/useSubscription';
import { calculateAge } from '../../utils/calculateAge';
import { BottomModal } from '../../components/shared/BottomModal';
import { ProfileAcceptanceLinks } from './ProfileAcceptanceLinks';
import { openTelegramLink } from '../../utils/openLink';
import { toast } from 'react-toastify';

const MENU_ITEMS = [
  {
    label: 'Мой профиль',
    icon: '/icons/profile_edit.svg',
    action: (ctx) => ctx.navigate('/ProfileEdit')
  },
  {
    label: 'Магазин подарков',
    icon: '/icons/profile_presents_shop.svg',
    action: (ctx) => ctx.setPresentsShop(true)
  },
  {
    label: 'Пригласить друга',
    icon: '/icons/profile_share.svg',
    action: (ctx) => ctx.navigate('/invite')
  },
  {
    label: 'Техническая поддержка',
    icon: '/icons/support.svg',
    action: () => openTelegramLink('https://t.me/myta_support')
  },
  {
    label: 'Управление подпиской',
    icon: '/icons/sub-icon.svg',
    condition: (ctx) => ctx.isActiveSubscription && !ctx.isLoading,
    action: (ctx) => ctx.navigate(ctx.routes.SUBSCRIPTION.MAIN)
  }
];

function ProfileMenu() {
  const navigate = useNavigate();

  const { isActiveSubscription, isLoading } = useSubscription();
  const { userId } = useSelector((state) => state.auth);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    if (!userId) return;
    try {
      const response = await axiosPrivate.get(`/users/${userId}`);
      setUserData(response.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [presentsShop, setPresentsShop] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  if (!userData) {
    return <div className="flex items-center justify-center h-screen text-white">Error loading user data</div>;
  }

  return (
    <div className="flex flex-1 h-[calc(100%-80px)] flex-col border-t border-[#233636] px-4 pt-4 pb-[26px] gap-6">
      <div className="flex-1 overflow-y-auto flex flex-col items-center gap-6">
        {!isActiveSubscription && !isLoading && (
          <img
            alt="Баннер премиум"
            src="/icons/premium-connect-banner.png"
            className="w-full"
            onClick={() => {
              navigate('/premium');
            }}
          />
        )}

        <div className="flex flex-row text-white w-full">
          <img
            alt="Аватар"
            src={userData.photos?.[0]}
            className="w-[64px] h-[64px] mr-[20px] rounded-full object-cover shrink-0"
          />
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex flex-col w-full min-w-0">
              <p className="text-[18px] font-medium truncate">{userData.name},</p>
              <p className="text-[18px] font-medium">{calculateAge(userData.birthDay)} лет</p>
            </div>
            <div className="flex flex-row mt-[2px] gap-[30px]">
              <div className="flex flex-col">
                <p className="opacity-50">Монеты:</p>
                <div className="flex flex-row text-[20px] items-center">
                  <img alt="Монета" src="/icons/coin.svg" className="mr-[4px] w-[20px] h-[20px]" />{' '}
                  {userData.wallet?.coins || 0}
                </div>
              </div>
              <div className="flex flex-col ">
                <p className="opacity-50">Монеты MYTA:</p>
                <div className="flex flex-row text-[20px] items-center">
                  <img alt="Монета MYTA" src="/icons/myta-coin.svg" className="mr-[4px] w-[20px] h-[20px]" />{' '}
                  {userData.wallet?.mytaCoins || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full flex items-center justify-center">
          <div
            className="bg-[#A1F69E1A] h-[44px] rounded-[400px] w-full text-[#A1F69E] text-[18px] flex flex-row items-center justify-center mt-[10px]"
            onClick={() => navigate('/makepayment')}
          >
            Пополнить баланс <img alt="Монета" src="/icons/coin.svg" className="ml-2 w-[20px] h-[20px]" />
          </div>
        </div>
        <div className="flex flex-col justify-start w-full text-white gap-[15px]">
          {MENU_ITEMS.map((item, index) => {
            const isVisible = item.condition ? item.condition({ isActiveSubscription, isLoading }) : true;

            if (!isVisible) return null;

            return (
              <div
                key={index}
                className="flex flex-row gap-1 items-center w-full cursor-pointer"
                onClick={() =>
                  item.action({
                    navigate,
                    setPresentsShop,
                    routes: config.ROUTES
                  })
                }
              >
                <img alt={item.label} src={item.icon} className="w-[32px] h-[32px]" />
                {item.label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex mt-auto flex-col gap-2 items-start">
        <ProfileAcceptanceLinks />
        <div className="flex flex-col border-t border-[#ffffff52] py-2 w-full">
          <Link to={config.ROUTES.PROFILE.DELETE} className="text-white text-sm font-medium">
            Удалить аккаунт
          </Link>
        </div>
      </div>

      {presentsShop && <GiftsShopModal onRefetchFn={fetchUserData} onClose={() => setPresentsShop(false)} />}

      <BottomModal isOpen={showPolicy} onClose={() => setShowPolicy(false)}>
        <>
          <h3 className="text-white text-xl font-raleway font-semibold px-4">Политика конфиденциальности</h3>
          <Policy />
        </>
      </BottomModal>
    </div>
  );
}

export default ProfileMenu;
